/**
 * Grand Quack Auto — chaos / wanted system.
 *
 * The meter that drives the whole game loop. Other systems call addChaos()
 * to increment points; this system decays them over time and updates the
 * wanted level based on thresholds.
 *
 * See research.md §3 "Wanted/chaos system (simplified GTA stars)".
 */

import { CHAOS_DECAY_PER_SEC, CHAOS_POINTS, WANTED_THRESHOLDS } from '../config';
import type { System, World } from '../types';
import { registerComboHit } from './comboSystem';
import { showPopup } from '../ui/scorePopups';

const WANTED_NOTIFICATIONS = [
  '',
  'A concerned citizen has reported... a duck.',
  'The police have escalated: "It\'s just quacking at people."',
  'THE NATIONAL GUARD HAS BEEN DEPLOYED TO CONTAIN... A RUBBER DUCK.',
];

let lastWanted = 0;

/** Update wanted level based on current chaos points. */
function updateWanted(world: World): void {
  const p = world.chaos.points;
  if (p >= WANTED_THRESHOLDS.chaosDuck) world.chaos.wanted = 3;
  else if (p >= WANTED_THRESHOLDS.menace) world.chaos.wanted = 2;
  else if (p >= WANTED_THRESHOLDS.nuisance) world.chaos.wanted = 1;
  else world.chaos.wanted = 0;

  // Flash notification on wanted level change.
  if (world.chaos.wanted > lastWanted && world.chaos.wanted > 0) {
    showWantedNotification(WANTED_NOTIFICATIONS[world.chaos.wanted] ?? '');
  }
  lastWanted = world.chaos.wanted;
}

function showWantedNotification(text: string): void {
  const hud = document.getElementById('hud');
  if (!hud || !text) return;
  const el = document.createElement('div');
  el.className = 'wanted-notification';
  el.textContent = text;
  hud.appendChild(el);
  setTimeout(() => el.remove(), 4000);
}

/**
 * Add chaos points by event kind. Called by quackSystem, npcSystem, etc.
 * Immediately updates the wanted level.
 */
export function addChaos(world: World, kind: keyof typeof CHAOS_POINTS): void {
  const multiplier = registerComboHit(world);
  world.chaos.points += CHAOS_POINTS[kind] * multiplier;
  updateWanted(world);
  showPopup(world, kind);
}

export const chaosSystem: System = {
  name: 'chaos',
  fixedUpdate(dt: number, world: World) {
    // Decay: points drop when below menace tier (★★) and calm.
    if (world.chaos.points > 0 && world.chaos.wanted < 2) {
      world.chaos.points = Math.max(0, world.chaos.points - CHAOS_DECAY_PER_SEC * dt);
      updateWanted(world);
    }
  },
};
